// Playing one video, in the one way this app plays videos — shared by the
// Note's viewer and a Document's video sheet so the rules exist once
// (docs/pla-video.md §4.5).
//
// The rules, in order:
//
//   1. The original plays first and always. It is what every video did before
//      the pipeline existed, and it stays the answer while Mux is still
//      working, when it was never configured, and when the reader may not have
//      a signed stream. This pipeline only ever adds.
//   2. The signed stream is asked for now, never baked into the page: a URL in
//      the HTML would go stale inside a Turbo snapshot, and asking at the
//      moment of playing is what makes losing access to a Card stop the next
//      play.
//   3. Safari and both iOS WebViews play HLS themselves. Everywhere else
//      hls.js is fetched on demand, self-hosted, in a chunk no page without a
//      video ever loads (docs/carrega-llibreries-esporadiques.md).
//   4. If the stream fails, the original comes back. A video that plays at the
//      wrong bitrate beats a black rectangle.
//
// Every step after an `await` asks whether it is still wanted: the reader may
// have closed the viewer, or opened another video, while we were waiting.
export class HlsPlayback {
  constructor(video) {
    this.video = video
    this.generation = 0
    this.hls = null
    this.originalUrl = null
  }

  // Bumped by the caller whenever what is on screen changes, so a late answer
  // can tell it is answering a question nobody is asking any more.
  begin() {
    this.generation++

    return this.generation
  }

  async play({ url, playbackUrl }) {
    const opening = this.begin()
    // Resolved, because `video.src` always reads back absolute: comparing it
    // against the relative path we were handed would make every guard below
    // answer "different" and this would restart a video that was already
    // playing, or restore an original that was already the source.
    this.originalUrl = new URL(url, document.baseURI).href
    if (this.video.src !== this.originalUrl) this.video.src = this.originalUrl
    if (!playbackUrl) return

    const streamUrl = await this.fetchStreamUrl(playbackUrl)
    if (!streamUrl || !this.isCurrent(opening)) return

    if (this.video.canPlayType("application/vnd.apple.mpegurl")) {
      this.video.src = streamUrl
    } else {
      await this.attachHls(streamUrl, opening)
    }
  }

  // Cannot loop: once restored the source *is* the original, and this refuses
  // to restore what is already playing.
  fallBackToOriginal(generation = this.generation) {
    if (!this.originalUrl || !this.isCurrent(generation)) return
    if (this.video.src === this.originalUrl) return

    this.detach()
    this.video.src = this.originalUrl
    this.video.load()
  }

  stop() {
    this.begin()
    this.originalUrl = null
    this.video.pause()
    this.detach()
    this.video.removeAttribute("src")
    this.video.load()
  }

  detach() {
    if (!this.hls) return

    this.hls.destroy()
    this.hls = null
  }

  isCurrent(generation) {
    return this.generation === generation && this.video.isConnected
  }

  async fetchStreamUrl(playbackUrl) {
    try {
      const response = await fetch(playbackUrl, { headers: { Accept: "application/json" } })
      if (!response.ok) return null

      return (await response.json()).url
    } catch {
      return null
    }
  }

  async attachHls(streamUrl, generation) {
    const { default: Hls } = await import("hls.js")
    // A megabyte of library takes its time, and the reader may have moved on.
    if (!Hls.isSupported() || !this.isCurrent(generation)) return

    this.detach()
    this.hls = new Hls()
    this.hls.on(Hls.Events.ERROR, (_event, data) => {
      if (data.fatal) this.fallBackToOriginal(generation)
    })
    this.hls.loadSource(streamUrl)
    this.hls.attachMedia(this.video)
  }
}
